import ExcelJS from 'exceljs'
import Papa from 'papaparse'
import iconv from 'iconv-lite'
import { saveAs } from 'file-saver'

/**
 * Exporta os dados para um arquivo Excel.
 *
 * @param {Object[]} datasets Conjuntos de dados a serem exportados.
 * @param {string} outputFilename Nome do arquivo de saída.
 *
 * @example
 * Estrutura de um dataset:
 *  {
 *    "options": {
 *      "title": "Título opcional da planilha exportada",
 *      "includes": [ nomes das colunas a incluir na planilha exportada ],
 *      "excludes": [ nomes das colunas a excluir da planilha exportada ]
 *    },
 *    "set": {
 *      "name": "Nome opcional do conjunto de dados",
 *      "title": "Título opcional do conjunto de dados",
 *      "headers": [
 *        { "name": "@row", "title": "Linha", "hidden": true },
 *        { "name": "colunaString", "title": "Coluna String", "dataType": "String" },
 *        { "name": "colunaNumero", "title": "Coluna Número", "dataType": "Number" },
 *        { "name": "colunaBoolean", "title": "Coluna Boolean", "dataType": "Boolean" },
 *        { "name": "colunaDateTime", "title": "Coluna DateTime", "dataType": "DateTime" },
 *        { "name": "colunaTexto", "title": "Coluna Texto", "dataType": "String" },
 *        { "name": "colunaInteiro", "title": "Coluna Inteiro", "dataType": "Integer" },
 *        { "name": "colunaDecimal", "title": "Coluna Decimal", "dataType": "Decimal" },
 *        { "name": "colunaShort", "title": "Coluna Short", "dataType": "Short" },
 *        { "name": "colunaLong", "title": "Coluna Long", "dataType": "Long" },
 *        { "name": "colunaMoeda", "title": "Coluna Moeda", "dataType": "Currency" },
 *        { "name": "colunaData", "title": "Coluna Data", "dataType": "Date" },
 *        { "name": "colunaHora", "title": "Coluna Hora", "dataType": "Time" },
 *        { "name": "colunaTimeSpan", "title": "Coluna TimeSpan", "dataType": "TimeSpan" }
 *      ],
 *      "rows": [
 *        {
 *          "@row": { "value": 1, "links": { eref: "ExemploDeLink", ... },
 *          "colunaString": "Exemplo String 1",
 *          "colunaNumero": 123.45,
 *          "colunaBoolean": true,
 *          "colunaDateTime": "2024-08-04T10:00:00Z",
 *          "colunaTexto": "Exemplo Texto 1",
 *          "colunaInteiro": 100,
 *          "colunaDecimal": 123.456,
 *          "colunaShort": 12,
 *          "colunaLong": 1234567890,
 *          "colunaMoeda": 99.99,
 *          "colunaData": "2024-08-04",
 *          "colunaHora": "10:00:00",
 *          "colunaTimeSpan": "10:00:00"
 *        },
 *        {
 *          "@row": { "value": 2, "links": { eref: "ExemploDeLink", ... },
 *          "colunaString": "Exemplo String 2",
 *          "colunaNumero": 678.90,
 *          "colunaBoolean": false,
 *          "colunaDateTime": "2024-08-05T11:00:00Z",
 *          "colunaTexto": "Exemplo Texto 2",
 *          "colunaInteiro": 200,
 *          "colunaDecimal": 789.012,
 *          "colunaShort": 34,
 *          "colunaLong": 9876543210,
 *          "colunaMoeda": 199.99,
 *          "colunaData": "2024-08-05",
 *          "colunaHora": "11:00:00",
 *          "colunaTimeSpan": "11:00:00"
 *        }
 *      ]
 *    }
 *  }
 */
export function exportToExcel(datasets, outputFilename) {
  if (!Array.isArray(datasets)) datasets = [datasets]
  outputFilename = outputFilename || 'dados.xlsx'

  const workbook = new ExcelJS.Workbook()

  datasets.forEach((dataset, index) => {
    const { options = {}, set } = dataset
    const includes = options.includes || []
    const excludes = options.excludes || ['@row']; // Excluir a coluna '@row' por padrão

    // Filtrar colunas conforme os includes e excludes
    const headers = set.headers.filter(header => {
      if (includes.length > 0 && !includes.includes(header.name)) return false
      if (excludes.length > 0 && excludes.includes(header.name)) return false
      return true
    })

    // Criar array de dados para exportação
    const data = set.rows.map(row => {
      const rowData = {}
      headers.forEach(header => {
        // O valor pode ser literal ou um objeto na forma { value: <valor>> }
        const cell = row[header.name]
        const value = (cell != null && typeof cell === 'object') ? cell.value : cell
        if (value === undefined) value = null
        rowData[header.title] = value;
      })
      return rowData
    })

    // Adicionar cabeçalhos e dados na planilha
    const worksheet = workbook.addWorksheet(options.title || set.title || set.name || `Planilha ${index + 1}`)

    // Adicionar cabeçalhos
    worksheet.columns = headers.map(header => ({
      header: header.title,
      key: header.title,
      width: Math.max(10, ...data.map(row => (row[header.title] ? row[header.title].toString().length : 10)))
    }))

    // Adicionar dados
    data.forEach(row => worksheet.addRow(row))

    // Adicionar estilo wrapText a todas as células e formatar de acordo com o tipo de dado
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell, colNumber) => {

        if (rowNumber === 1) {
          // Formatar cabeçalho
          cell.font = { bold: true }
          cell.alignment = { wrapText: true }
        } else {
          const header = headers[colNumber - 1]
          const valorCelula = cell.value

          // Aplicar formatação de acordo com o tipo de dado
          switch (header.dataType?.toLowerCase()) {
            case 'string':
            case 'text':
              cell.value = valorCelula ? valorCelula.toString() : ''
              break
            case 'boolean':
              cell.value = Boolean(valorCelula)
              break
            case 'integer':
              cell.value = parseInt(valorCelula, 10)
              cell.numFmt = '0'
              break
            case 'decimal':
            case 'number':
              cell.value = parseFloat(valorCelula)
              cell.numFmt = '0.00'
              break
            case 'datetime':
              cell.value = new Date(valorCelula)
              cell.numFmt = 'dd/MM/yyyy HH:mm:ss'
              break
            case 'date':
              cell.value = new Date(valorCelula)
              cell.numFmt = 'dd/MM/yyyy'
              break
            case 'time':
              cell.value = new Date(`1970-01-01T${valorCelula}`)
              cell.numFmt = 'HH:mm:ss'
              break
            case 'timespan':
              cell.value = valorCelula ? valorCelula.toString() : ''
              break
            default:
              cell.value = valorCelula ? valorCelula.toString() : ''
          }
        }
      })
    })
  })

  // Gerar arquivo Excel como um Blob
  workbook.xlsx.writeBuffer().then(buffer => {
    const blob = new Blob([buffer], { type: 'application/octet-stream' })
    saveAs(blob, outputFilename)
  })
}

/**
 * Exporta os dados para um arquivo CSV.
 *
 * @param {Object[]} datasets Conjuntos de dados a serem exportados.
 * @param {string} outputFilename Nome do arquivo de saída.
 *
 * @example
 * Estrutura de um dataset:
 *  {
 *    "options": {
 *      "title": "Título opcional da planilha exportada",
 *      "includes": [ nomes das colunas a incluir na planilha exportada ],
 *      "excludes": [ nomes das colunas a excluir da planilha exportada ]
 *    },
 *    "set": {
 *      "name": "Nome opcional do conjunto de dados",
 *      "title": "Título opcional do conjunto de dados",
 *      "headers": [
 *        { "name": "@row", "title": "Linha", "hidden": true },
 *        { "name": "colunaString", "title": "Coluna String", "dataType": "String" },
 *        { "name": "colunaNumero", "title": "Coluna Número", "dataType": "Number" },
 *        { "name": "colunaBoolean", "title": "Coluna Boolean", "dataType": "Boolean" },
 *        { "name": "colunaDateTime", "title": "Coluna DateTime", "dataType": "DateTime" },
 *        { "name": "colunaTexto", "title": "Coluna Texto", "dataType": "String" },
 *        { "name": "colunaInteiro", "title": "Coluna Inteiro", "dataType": "Integer" },
 *        { "name": "colunaDecimal", "title": "Coluna Decimal", "dataType": "Decimal" },
 *        { "name": "colunaShort", "title": "Coluna Short", "dataType": "Short" },
 *        { "name": "colunaLong", "title": "Coluna Long", "dataType": "Long" },
 *        { "name": "colunaMoeda", "title": "Coluna Moeda", "dataType": "Currency" },
 *        { "name": "colunaData", "title": "Coluna Data", "dataType": "Date" },
 *        { "name": "colunaHora", "title": "Coluna Hora", "dataType": "Time" },
 *        { "name": "colunaTimeSpan", "title": "Coluna TimeSpan", "dataType": "TimeSpan" }
 *      ],
 *      "rows": [
 *        {
 *          "@row": { "value": 1, "links": { eref: "ExemploDeLink", ... },
 *          "colunaString": "Exemplo String 1",
 *          "colunaNumero": 123.45,
 *          "colunaBoolean": true,
 *          "colunaDateTime": "2024-08-04T10:00:00Z",
 *          "colunaTexto": "Exemplo Texto 1",
 *          "colunaInteiro": 100,
 *          "colunaDecimal": 123.456,
 *          "colunaShort": 12,
 *          "colunaLong": 1234567890,
 *          "colunaMoeda": 99.99,
 *          "colunaData": "2024-08-04",
 *          "colunaHora": "10:00:00",
 *          "colunaTimeSpan": "10:00:00"
 *        },
 *        {
 *          "@row": { "value": 2, "links": { eref: "ExemploDeLink", ... },
 *          "colunaString": "Exemplo String 2",
 *          "colunaNumero": 678.90,
 *          "colunaBoolean": false,
 *          "colunaDateTime": "2024-08-05T11:00:00Z",
 *          "colunaTexto": "Exemplo Texto 2",
 *          "colunaInteiro": 200,
 *          "colunaDecimal": 789.012,
 *          "colunaShort": 34,
 *          "colunaLong": 9876543210,
 *          "colunaMoeda": 199.99,
 *          "colunaData": "2024-08-05",
 *          "colunaHora": "11:00:00",
 *          "colunaTimeSpan": "11:00:00"
 *        }
 *      ]
 *    }
 *  }
 */
export function exportToCsv(datasets, outputFilename) {
  if (!Array.isArray(datasets)) datasets = [datasets]

  datasets.forEach((dataset, index) => {
    const { options = {}, set } = dataset
    const includes = options.includes || []
    const excludes = options.excludes || ['@row']; // Excluir a coluna '@row' por padrão

    // Filtrar colunas conforme os includes e excludes
    const headers = set.headers.filter(header => {
      if (includes.length > 0 && !includes.includes(header.name)) return false
      if (excludes.length > 0 && excludes.includes(header.name)) return false
      return true
    })

    // Criar array de dados para exportação
    const data = set.rows.map(row => {
      const rowData = {}
      headers.forEach(header => {
        // O valor pode ser literal ou um objeto na forma { value: <valor>> }
        const cell = row[header.name]
        rowData[header.title] = (cell != null && typeof cell === 'object')
            ? (cell.value || null) : (cell || null);
      })
      return rowData
    })

    // Adicionar cabeçalhos e dados
    const csvData = [headers.map(header => header.title), ...data.map(Object.values)]
    const csv = Papa.unparse(csvData, {
      header: true,
      delimiter: ',',
      quotes: true
    })

    // Converter CSV para encoding ISO-8859-1
    const csvEncoded = iconv.encode(csv, 'ISO-8859-1')

    // Definir título do arquivo baseado no conjunto de dados
    const sheetName = options.title || set.title || set.name || `Planilha_${index + 1}`
    const fileName = index === 0 ? outputFilename : `${outputFilename.replace('.csv', '')}_${sheetName}.csv`

    // Criar arquivo CSV como um Blob
    const blob = new Blob([csvEncoded], { type: 'text/csv;charset=ISO-8859-1;' })
    saveAs(blob, fileName)
  })
}
